<template>
  <div class="page-container">
    <PageHeader title="存档状态">
      <template #actions>
        <ArchiveAppSelect v-model="currentApp" @change="handleAppChange" />
        <el-button size="small" icon="el-icon-refresh" :loading="statusLoading" @click="fetchStatus">刷新</el-button>
      </template>
    </PageHeader>

    <div v-if="!currentApp" class="qw-archive-status__placeholder">
      <TablePanel>
        <EmptyState
          icon="el-icon-connection"
          title="请先选择接入方"
          description="会话存档接口需以接入方身份调用（携带 appId/appSecret），请从右上角选择"
        />
      </TablePanel>
    </div>

    <template v-else>
      <!-- 拉取状态卡片（qw_archive_cursor 游标信息）：qw-card 基类 + 体系四色渐变图标位 -->
      <div class="qw-archive-status__cards">
        <div class="qw-card qw-archive-status__card">
          <div class="qw-archive-status__card-icon qw-archive-status__card-icon--blue">
            <i class="el-icon-loading"></i>
          </div>
          <div class="qw-archive-status__card-info">
            <div class="qw-archive-status__card-value">{{ cursor.seq }}</div>
            <div class="qw-archive-status__card-label">当前游标 seq</div>
          </div>
        </div>
        <div class="qw-card qw-archive-status__card">
          <div class="qw-archive-status__card-icon qw-archive-status__card-icon--indigo">
            <i class="el-icon-download"></i>
          </div>
          <div class="qw-archive-status__card-info">
            <div class="qw-archive-status__card-value">{{ cursor.lastPullCount }}</div>
            <div class="qw-archive-status__card-label">上次拉取条数</div>
          </div>
        </div>
        <div class="qw-card qw-archive-status__card">
          <div class="qw-archive-status__card-icon qw-archive-status__card-icon--cyan">
            <i class="el-icon-files"></i>
          </div>
          <div class="qw-archive-status__card-info">
            <div class="qw-archive-status__card-value">{{ cursor.totalCount }}</div>
            <div class="qw-archive-status__card-label">累计入库消息</div>
          </div>
        </div>
        <div class="qw-card qw-archive-status__card">
          <div class="qw-archive-status__card-icon qw-archive-status__card-icon--emerald">
            <i class="el-icon-time"></i>
          </div>
          <div class="qw-archive-status__card-info">
            <div class="qw-archive-status__card-value qw-archive-status__card-time">{{ formatDateTime(cursor.lastPullTime) }}</div>
            <div class="qw-archive-status__card-label">最近拉取时间</div>
          </div>
        </div>
      </div>

      <!-- 手动触发同步操作条 -->
      <div class="qw-card qw-archive-status__actions">
        <el-button
          type="primary"
          size="small"
          icon="el-icon-refresh"
          :loading="pullLoading"
          :disabled="!currentApp"
          @click="handleTriggerPull"
        >手动拉取消息</el-button>
        <el-button
          size="small"
          icon="el-icon-sort"
          :loading="syncLoading"
          :disabled="!currentApp"
          @click="handleTriggerSync"
        >同步客户群</el-button>
        <span class="qw-archive-status__tip">定时任务会自动同步；手动触发用于立即增量拉取一次</span>
      </div>

      <!-- 群维度消息统计 -->
      <TablePanel title="群维度消息统计">
        <el-table v-loading="roomsLoading" :data="roomStats" stripe>
          <el-table-column label="群名称" min-width="200" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.groupName || '未同步群名' }}</template>
          </el-table-column>
          <el-table-column label="roomid（chatId）" min-width="220" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <span class="qw-copy-text">{{ row.roomid }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="msgCount" label="消息数" min-width="90" align="center" sortable />
          <el-table-column prop="externalCount" label="外部消息数" min-width="105" align="center" />
          <el-table-column label="最早消息" min-width="160">
            <template slot-scope="{ row }">{{ formatDateTime(row.firstMsgtime) }}</template>
          </el-table-column>
          <el-table-column label="最近消息" min-width="160">
            <template slot-scope="{ row }">{{ formatDateTime(row.lastMsgtime) }}</template>
          </el-table-column>
        </el-table>
      </TablePanel>
    </template>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import TablePanel from '@/components/TablePanel.vue'
import EmptyState from '@/components/EmptyState.vue'
import ArchiveAppSelect from '@/components/ArchiveAppSelect.vue'
import { getArchiveStatus, triggerPull, triggerGroupSync, getMessageRooms } from '@/api/archive'
import { formatDateTime } from '@/utils/format'

// 存档状态：拉取游标信息 + 手动触发拉取/同步 + 群维度统计（/qw-archive/status|trigger/*|message/rooms）
export default {
    name: 'ArchiveStatus',
    components: { PageHeader, TablePanel, EmptyState, ArchiveAppSelect },
    data () {
        return {
            currentApp: null,
            statusLoading: false,
            pullLoading: false,
            syncLoading: false,
            roomsLoading: false,
            cursor: { seq: '-', lastPullTime: null, lastPullCount: '-', totalCount: '-', remark: '' },
            roomStats: []
        }
    },
    methods: {
        formatDateTime,
        handleAppChange () {
            if (this.currentApp) {
                this.fetchStatus()
                this.fetchRoomStats()
            } else {
                this.cursor = { seq: '-', lastPullTime: null, lastPullCount: '-', totalCount: '-', remark: '' }
                this.roomStats = []
            }
        },
        async fetchStatus () {
            if (!this.currentApp) return
            this.statusLoading = true
            try {
                const res = await getArchiveStatus(this.currentApp)
                const data = res.data || {}
                this.cursor = {
                    seq: data.seq != null ? data.seq : '-',
                    lastPullTime: data.lastPullTime || null,
                    lastPullCount: data.lastPullCount != null ? data.lastPullCount : '-',
                    totalCount: data.totalCount != null ? data.totalCount : '-',
                    remark: data.remark || ''
                }
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.statusLoading = false
            }
        },
        async fetchRoomStats () {
            if (!this.currentApp) return
            this.roomsLoading = true
            try {
                const res = await getMessageRooms(this.currentApp)
                this.roomStats = res.data || []
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.roomsLoading = false
            }
        },
        // 手动拉取：立即增量拉取一次存档消息，返回新入库条数
        async handleTriggerPull () {
            this.pullLoading = true
            try {
                const res = await triggerPull(this.currentApp)
                this.$message.success(`拉取完成，新入库 ${res.data != null ? res.data : 0} 条`)
                this.fetchStatus()
                this.fetchRoomStats()
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.pullLoading = false
            }
        },
        // 手动同步客户群：立即同步群列表 + 详情
        async handleTriggerSync () {
            this.syncLoading = true
            try {
                const res = await triggerGroupSync(this.currentApp)
                this.$message.success(`同步完成，共 ${res.data != null ? res.data : 0} 个群`)
                this.fetchRoomStats()
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.syncLoading = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
// 统计卡网格：卡片边框/圆角/阴影由全局 .qw-card 接管，这里只管布局
.qw-archive-status__cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
}

.qw-archive-status__card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
}

.qw-archive-status__card-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    color: #fff;
    flex-shrink: 0;

    &--blue {
        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
        box-shadow: 0 8px 16px rgba(37, 99, 235, 0.24);
    }

    &--indigo {
        background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
        box-shadow: 0 8px 16px rgba(79, 70, 229, 0.24);
    }

    &--cyan {
        background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
        box-shadow: 0 8px 16px rgba(6, 182, 212, 0.24);
    }

    &--emerald {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        box-shadow: 0 8px 16px rgba(16, 185, 129, 0.24);
    }
}

.qw-archive-status__card-info {
    min-width: 0;
}

.qw-archive-status__card-value {
    font-size: 22px;
    font-weight: 700;
    color: var(--qw-text-primary);
    line-height: 1.2;
}

.qw-archive-status__card-time {
    font-size: 15px;
}

.qw-archive-status__card-label {
    margin-top: 4px;
    font-size: 12px;
    color: var(--qw-text-tertiary);
}

// 手动拉取/同步操作条：卡片化容器 + 左按钮右提示
.qw-archive-status__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    margin-bottom: 16px;
}

.qw-archive-status__tip {
    margin-left: auto;
    font-size: 12px;
    color: var(--qw-text-tertiary);
}
</style>
